import React from "react";
import { useTranslation } from "react-i18next";
import type {
  LineSummary,
  TimelineSummary,
  MachineSummaryRow,
  CulpritMachine,
  StopCause,
} from "../../services/ShiftAnalysisService";
import { CountUp } from "./anim";

interface Props {
  line: LineSummary | null;
  timeline: TimelineSummary | null;
  machines: MachineSummaryRow[];
  culprit?: CulpritMachine;
  topCause?: StopCause;
  onHover: (sn: string | null) => void;
  onSelect: (sn: string) => void;
}

const CARD: React.CSSProperties = {
  backgroundColor: "var(--bg-primary)",
  borderColor: "var(--border-primary)",
  boxShadow: "0 1px 2px rgba(16,24,40,0.06), 0 4px 12px rgba(16,24,40,0.05)",
};

function fmtDowntime(ms: number): string {
  const min = Math.round(ms / 60000);
  if (min < 60) return `${min}m`;
  const h = Math.floor(min / 60);
  return `${h}h ${min % 60}m`;
}

/** One KPI: uppercase micro-label above a bold value. */
const Kpi: React.FC<{
  label: string;
  value: React.ReactNode;
  accent?: string;
}> = ({ label, value, accent }) => (
  <div className="flex flex-col gap-0.5 min-w-[74px]">
    <span
      className="text-[10px] font-semibold uppercase tracking-[0.12em]"
      style={{ color: "var(--text-secondary)" }}
    >
      {label}
    </span>
    <span
      className="text-2xl font-extrabold tabular-nums leading-none tracking-tight"
      style={{ color: accent ?? "var(--text-primary)" }}
    >
      {value}
    </span>
  </div>
);

const Chip: React.FC<{ n: number; label: string; color: string }> = ({
  n,
  label,
  color,
}) =>
  n > 0 ? (
    <span
      className="inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-xs font-semibold"
      style={{ backgroundColor: `${color}1a`, color }}
    >
      <span
        className="w-1.5 h-1.5 rounded-full"
        style={{ backgroundColor: color }}
      />
      {n} {label}
    </span>
  ) : null;

const ShiftVerdictHeader: React.FC<Props> = ({
  line,
  timeline,
  machines,
  culprit,
  topCause,
  onHover,
  onSelect,
}) => {
  const { t } = useTranslation();

  const output = machines.reduce((s, m) => s + m.output, 0);
  const effPct = (line?.avg_efficiency ?? 0) * 100;
  const utilPct = (line?.avg_utilization ?? 0) * 100;
  const waste = line?.total_waste ?? 0;
  const downtimeMs = line?.total_downtime_ms ?? 0;
  const faults = timeline?.total_faults ?? 0;

  const good = line?.good_count ?? 0;
  const watch = line?.watch_count ?? 0;
  const bad = line?.bad_count ?? 0;
  const noData = line?.no_data_count ?? 0;

  const producing = output > 0;
  const verdict = !producing
    ? {
        tone: "#64748b",
        title: t("verdictIdle", "No production recorded"),
        sub: t("verdictIdleSub", "The line reported no output this shift."),
      }
    : bad > 0
      ? {
          tone: "#ef4444",
          title: t("verdictBad", {
            defaultValue: "{{n}} machine(s) need attention",
            n: bad,
          }),
          sub: culprit
            ? t("verdictCulprit", {
                defaultValue: "Biggest stopper: {{name}} · {{stops}} stops",
                name: culprit.machine_name,
                stops: culprit.internal_stops,
              })
            : topCause?.fault_text,
        }
      : watch > 0
        ? {
            tone: "#eab308",
            title: t("verdictWatch", {
              defaultValue: "Running — {{n}} to watch",
              n: watch,
            }),
            sub: topCause?.fault_text,
          }
        : {
            tone: "#22c55e",
            title: t("verdictHealthy", "Healthy shift"),
            sub: t("verdictHealthySub", "All machines within target."),
          };

  const effColor =
    effPct >= 75 ? "#22c55e" : effPct >= 45 ? "#eab308" : "#ef4444";

  return (
    <div
      className="relative rounded-2xl border p-5 overflow-hidden"
      style={CARD}
    >
      {/* Tone-tinted glow accent */}
      <div
        aria-hidden
        className="absolute -top-24 -left-24 w-72 h-72 rounded-full pointer-events-none"
        style={{
          background: `radial-gradient(circle, ${verdict.tone}22, transparent 70%)`,
        }}
      />
      <div
        aria-hidden
        className="absolute left-0 top-0 bottom-0 w-1.5"
        style={{ backgroundColor: verdict.tone }}
      />
      <div className="relative flex flex-wrap items-center gap-x-8 gap-y-5 pl-2">
        {/* Verdict */}
        <div className="min-w-[220px]">
          <div
            className="text-[10px] font-semibold uppercase tracking-[0.14em]"
            style={{ color: "var(--text-secondary)" }}
          >
            {t("shiftVerdict", "Shift verdict")}
          </div>
          <div className="flex items-center gap-2.5 mt-1.5">
            <span
              className="w-3 h-3 rounded-full shrink-0"
              style={{
                backgroundColor: verdict.tone,
                boxShadow: `0 0 0 4px ${verdict.tone}26`,
              }}
            />
            <span
              className="text-2xl font-extrabold leading-none tracking-tight"
              style={{ color: "var(--text-primary)" }}
            >
              {verdict.title}
            </span>
          </div>
          {verdict.sub && (
            <button
              type="button"
              onClick={() => culprit && onSelect(culprit.machine_sn)}
              onMouseEnter={() => culprit && onHover(culprit.machine_sn)}
              onMouseLeave={() => onHover(null)}
              className="text-xs mt-1 text-left hover:underline"
              style={{ color: "var(--text-secondary)" }}
            >
              {verdict.sub}
            </button>
          )}
        </div>

        {/* Divider */}
        <div
          className="hidden sm:block self-stretch w-px"
          style={{ backgroundColor: "var(--border-primary)" }}
        />

        {/* KPI strip */}
        <div className="flex flex-wrap items-center gap-x-7 gap-y-4">
          <Kpi
            label={t("efficiency", "Efficiency")}
            value={
              <CountUp value={effPct} format={(n) => `${Math.round(n)}%`} />
            }
            accent={effColor}
          />
          <Kpi
            label={t("utilization", "Utilization")}
            value={
              <CountUp value={utilPct} format={(n) => `${Math.round(n)}%`} />
            }
          />
          <Kpi
            label={t("output", "Output")}
            value={
              <CountUp
                value={output}
                format={(n) => Math.round(n).toLocaleString()}
              />
            }
          />
          <Kpi
            label={t("waste", "Waste")}
            value={
              <CountUp
                value={waste}
                format={(n) => Math.round(n).toLocaleString()}
              />
            }
            accent={waste > 0 ? "#f97316" : undefined}
          />
          <Kpi
            label={t("downtime", "Downtime")}
            value={fmtDowntime(downtimeMs)}
          />
          <Kpi
            label={t("faults", "Faults")}
            value={
              <CountUp value={faults} format={(n) => String(Math.round(n))} />
            }
            accent={faults > 0 ? "#ef4444" : undefined}
          />
        </div>

        {/* Health chips */}
        <div className="flex flex-wrap items-center gap-2 ml-auto">
          <Chip n={good} label={t("statusGood", "Good")} color="#22c55e" />
          <Chip n={watch} label={t("statusWatch", "Watch")} color="#eab308" />
          <Chip n={bad} label={t("statusBad", "Bad")} color="#ef4444" />
          <Chip
            n={noData}
            label={t("statusNoData", "No data")}
            color="#94a3b8"
          />
        </div>
      </div>
    </div>
  );
};

export default ShiftVerdictHeader;
